
import React, { useState, useMemo } from 'react';
import { Article, CostParams, CostBreakdown } from '../types.ts';
import { calculateCostBreakdown } from '../services/costCalculator.ts';

// Helper components for layout
const InputRow: React.FC<{ label: string, name: string, value: number, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, unit: string, step?: string }> = 
({ label, name, value, onChange, unit, step = "1" }) => (
  <div className="flex items-center justify-between gap-2 py-1">
    <label htmlFor={`sim-${name}`} className="text-sm text-gray-600">{label}</label>
    <div className="relative w-32">
      <input 
        type="number" 
        name={name} 
        id={`sim-${name}`} 
        value={value} 
        onChange={onChange} 
        step={step} 
        className="block w-full p-2 text-right border border-gray-300 rounded-md bg-white pr-10"
      />
      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
        <span className="text-gray-500 sm:text-sm">{unit}</span>
      </div>
    </div>
  </div>
);

const ResultRow: React.FC<{ label: string, value: number, isSubtotal?: boolean, isTotal?: boolean }> = 
({ label, value, isSubtotal = false, isTotal = false }) => (
  <div className={`flex justify-between items-center py-2 px-3 my-1 rounded-md ${
    isTotal ? 'bg-sky-100 text-sky-800' : 
    isSubtotal ? 'bg-gray-100' : ''
  }`}>
    <span className={`font-semibold ${isTotal ? 'text-lg' : 'text-sm'}`}>{label}</span>
    <span className={`font-mono font-bold ${isTotal ? 'text-lg' : 'text-base'}`}>
      € {value.toFixed(3)}
    </span>
  </div>
);

const Operator: React.FC<{ symbol: 'plus' | 'equals' }> = ({ symbol }) => (
    <div className="flex justify-center items-center my-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xl font-light ${
            symbol === 'plus' ? 'bg-gray-200 text-gray-600' : 'bg-sky-500 text-white'
        }`}>
            <i className={`fas fa-${symbol}`}></i>
        </div>
    </div>
);


const CostSimulator: React.FC<{ costParams: CostParams }> = ({ costParams }) => {
    const [simParams, setSimParams] = useState({
        warpWeightGpm: 250,
        weftWeightGpm: 170,
        elastanePercentage: 2,
        finishingCostPerMeter: 0.80,
    });

    const handleParamChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setSimParams(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    };

    // Calculate everything in real-time using useMemo
    const breakdown: CostBreakdown = useMemo(() => {
        const simulatedArticle: Article = { // Dummy article for calculation
            id: 'simulated',
            name: 'Articolo Simualto',
            code: 'SIMULATED|N/A',
            status: 'active',
            managed: true,
            composition: `${100-simParams.elastanePercentage}% CO, ${simParams.elastanePercentage}% EL`,
            weightOz: 0,
            finishingCostPerMeter: simParams.finishingCostPerMeter,
            details: {
                elasticity: 'N/A', drawing: 'N/A', warp: 'N/A', weft: 'N/A', color: 'N/A', finishing: 'N/A',
                cottonPercentage: 100 - simParams.elastanePercentage,
                elastanePercentage: simParams.elastanePercentage,
                warpWeightGpm: simParams.warpWeightGpm,
                weftWeightGpm: simParams.weftWeightGpm,
            }
        };
        return calculateCostBreakdown(simulatedArticle, costParams);
    }, [simParams, costParams]);

    return (
        <div className="bg-white p-4 rounded-lg shadow-lg h-full flex flex-col">
            <h3 className="text-xl font-bold mb-4 text-gray-800 border-b pb-2">Simulatore Costo Interattivo</h3>
            
            <div className="space-y-2 flex-grow">
                {/* Section 1: Yarn */}
                <div>
                    <h4 className="font-bold text-gray-600 mb-2">1. Materia Prima (Filato)</h4>
                    <InputRow label="Peso Ordito" name="warpWeightGpm" value={simParams.warpWeightGpm} onChange={handleParamChange} unit="g/m" />
                    <InputRow label="Peso Trama" name="weftWeightGpm" value={simParams.weftWeightGpm} onChange={handleParamChange} unit="g/m" />
                    <InputRow label="% Elastomero" name="elastanePercentage" value={simParams.elastanePercentage} onChange={handleParamChange} unit="%" step="0.5" />
                    <ResultRow label="Costo Totale Filato" value={breakdown.totalYarnCost} isSubtotal />
                </div>

                <Operator symbol="plus" />

                {/* Section 2: Weaving */}
                <div>
                    <h4 className="font-bold text-gray-600 mb-2">2. Tessitura</h4>
                    <ResultRow label="Costo Post-Tessitura" value={breakdown.costAfterWeavingWaste} isSubtotal />
                </div>
                
                <Operator symbol="plus" />

                {/* Section 3: Finishing */}
                <div>
                     <h4 className="font-bold text-gray-600 mb-2">3. Finissaggio</h4>
                     <InputRow label="Costo Finissaggio" name="finishingCostPerMeter" value={simParams.finishingCostPerMeter} onChange={handleParamChange} unit="€/m" step="0.01" />
                     <ResultRow label="Costo Tessuto Finito" value={breakdown.subtotal} isSubtotal />
                </div>

                {/* Section 4: Expenses (Optional) */}
                {costParams.includeGeneralExpenses && (
                    <>
                        <Operator symbol="plus" />
                        <div>
                            <h4 className="font-bold text-gray-600 mb-2">4. Spese Generali ({costParams.generalExpensesPercentage}%)</h4>
                            <ResultRow label="Ammontare Spese" value={breakdown.generalExpensesAmount} isSubtotal />
                        </div>
                    </>
                )}

                <Operator symbol="equals" />

                {/* Final Result */}
                <ResultRow label="Costo Finale /m" value={breakdown.finalCost} isTotal />
            </div>
        </div>
    );
};

export default CostSimulator;
